import { useMemo, useState } from 'react';
import { money, route } from '../api';
import { useStore } from '../store';
import type { Fit, Selection, Version } from '../types';
import { Media } from '../components/Media';
import { versions } from '../components/ProductCard';
import { Icon } from '../components/Icons';
import { Link } from '../components/Link';
import { resolveVersion } from '../productVariants';
import { resolveSizeForFit } from '../sizing';
import { FitAndSize } from '../components/FitAndSize';
import { useLanguage } from '../i18n';

export function ProductPage({ id }: { id: string }) {
  const { products, add } = useStore();
  const { t } = useLanguage();
  const product = products.find(p => p.id === id);
  const available = product ? versions(product) : [];
  const [chosenVersion, setVersion] = useState<Version | null>(null);
  const [chosenFit, setFit] = useState<Fit>('sin_especificar');
  const [chosenSize, setSize] = useState('M');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // El catálogo llega después del primer render: nunca conservamos una variante inexistente.
  const version = product ? resolveVersion(product, chosenVersion) : 'fan';
  const size = product ? resolveSizeForFit(product, chosenFit, chosenSize) : 'M';
  const total = useMemo(
    () => product ? Number(product[version] || 0) + Number(product.sizePrices?.[size] || 0) : 0,
    [product, version, size]
  );
  if (!product) return <NotFound />;
  const selection: Selection = { productId: product.id, version, fit: chosenFit, size, name: '', number: '', patches: [] };
  async function addPlain() {
    if (chosenFit === 'sin_especificar') { setError(t('chooseFit')); return; }
    setBusy(true);
    setError('');
    try { await add(selection); route('/carrito'); }
    catch (cause) { setError(cause instanceof Error ? cause.message : t('loadError')); }
    finally { setBusy(false); }
  }
  return <section className="product-page shell">
    <nav className="breadcrumbs"><Link href="/">{t('home')}</Link><span>/</span><Link href={`/catalogo?category=${product.category}`}>{product.category}</Link><span>/</span>{product.title}</nav>
    <div className="product-layout">
      <div className="gallery"><div className="thumb"><Media id={product.cardImage || product.front} alt={`Vista de ${product.title}`} /></div><div className="main-photo"><Media id={product.cardImage || product.front} alt={product.title} /></div></div>
      <div className="product-details">
        <p className="availability">● {product.availability === 'por-encargo' ? t('madeToOrder') : t('available')}</p>
        <h1>{product.title}</h1><p className="subtitle">{product.team} · {product.season} · {product.kitType}</p>
        <strong className="big-price">{money(total)}</strong>
        <p>{available.length === 1 ? t('versionOnly',{version:available[0][0] === 'fan' ? 'Fan' : 'Player'}) : t('bothVersions')}</p>
        <fieldset><legend>1. {t('chooseVersion')}</legend><div className="version-options">{available.map(([name, price]) => <label className={version === name ? 'selected' : ''} key={name}><input type="radio" checked={version === name} onChange={() => setVersion(name)} /><b>{name === 'fan' ? 'Fan' : 'Player'} · {money(price)}</b><small>{name === 'fan' ? t('fanCut') : t('playerCut')}</small></label>)}</div></fieldset>
        <fieldset><legend>2. {t('chooseFit')}</legend><FitAndSize product={product} fit={chosenFit} size={size} onChange={(fit, nextSize) => { setFit(fit); setSize(nextSize); setError(''); }} priceLabel={value => product.sizePrices?.[value] ? ` · +${money(product.sizePrices[value])}` : ''} /></fieldset>
        <fieldset><legend>3. {t('customizeQuestion')}</legend><div className="product-actions">{chosenFit === 'sin_especificar' ? <button className="button primary" disabled>{t('chooseFitFirst')}</button> : <Link className="button primary" href={`/personalizar/${product.id}?version=${version}&fit=${chosenFit}&size=${size}`}>{t('customizeJersey')} <Icon name="arrow" /></Link>}<button className="button outline" disabled={busy || chosenFit === 'sin_especificar'} onClick={() => void addPlain()}>{busy ? t('adding') : t('addPlain')}</button></div>{error && <p className="form-error" role="alert">{error}</p>}</fieldset>
        <div className="delivery"><span><Icon name="truck" /><b>{t('estimated')}</b><small>{product.deliveryMinDays}–{product.deliveryMaxDays} {t('days')}</small></span><span><Icon name="shield" /><b>{t('guidedPurchase')}</b><small>{t('confirmedDetails')}</small></span></div>
      </div>
    </div>
  </section>;
}

function NotFound() { const {t}=useLanguage(); return <div className="empty page"><b>{t('noProducts')}</b><Link className="button primary" href="/catalogo">{t('catalog')}</Link></div>; }
